/**
 * utils/smartTableParser.ts
 *
 * Parses CSV, TSV, and Excel files into SmartTableRow objects for use
 * in the smart table form builder. Handles column auto-detection from
 * common French and English header names.
 *
 * Created April 2026: supports the smart-table form creation workflow.
 * Updated April 2026: added conditionRaw field and Condition column detection.
 * Updated April 2026: added `name` (Intitulé) field distinct from `label` (Question),
 *   mirroring the catalog structure (name = admin title, label = client-facing question).
 * Updated April 2026: tightened detectColumnIndex to whole-word matching to prevent
 *   false positives (e.g. "Type de document" matching the name column); removed overly
 *   broad nameIdx keywords; fixed row[0] fallback to only apply when no name/label column
 *   exists; added CSV parse-error rejection; Excel sheet selection now skips cover sheets.
 * Updated April 2026: added stable `id` field to SmartTableRow for React key stability;
 *   added MAX_EXCEL_FILE_SIZE guard in parseExcel to reject oversized workbooks.
 * Updated April 2026: parseExcel now rejects with a descriptive error when the
 *   FileReader yields a null result instead of silently resolving with [].
 * Updated April 2026: added `isRequired` field with Obligatoire column detection;
 *   options now split on comma only (semicolon and pipe removed as separators).
 */

import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type { FieldType } from '../types/database';

export interface SmartTableRow {
  id: string;
  name: string;
  label: string;
  type: FieldType;
  description: string;
  options: string[];
  conditionRaw: string;
  isRequired: boolean;
}

const MAX_EXCEL_FILE_SIZE = 10 * 1024 * 1024;

const FIELD_TYPE_LABELS: Record<string, FieldType> = {
  document: 'document',
  doc: 'document',
  fichier: 'document',
  file: 'document',
  texte: 'text',
  text: 'text',
  'texte long': 'text',
  'long text': 'text',
  textarea: 'text',
  'texte court': 'short_text',
  'short text': 'short_text',
  short_text: 'short_text',
  court: 'short_text',
  date: 'date',
  'choix unique': 'single_choice',
  'single choice': 'single_choice',
  single_choice: 'single_choice',
  'choix simple': 'single_choice',
  radio: 'single_choice',
  'choix multiple': 'multiple_choice',
  'multiple choice': 'multiple_choice',
  multiple_choice: 'multiple_choice',
  checkbox: 'multiple_choice',
  cases: 'multiple_choice',
};

const FALSY_VALUES = new Set(['non', 'no', 'false', '0']);

function normalizeType(raw: string): FieldType {
  const normalized = raw.trim().toLowerCase();
  return FIELD_TYPE_LABELS[normalized] ?? 'document';
}

function matchesKeyword(header: string, keyword: string): boolean {
  if (header === keyword) return true;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?:^|\\s)${escaped}(?:\\s|$)`, 'i').test(header);
}

function detectColumnIndex(headers: string[], keywords: string[]): number {
  const lower = headers.map((h) => h.trim().toLowerCase());
  for (const kw of keywords) {
    const idx = lower.findIndex((h) => matchesKeyword(h, kw.toLowerCase()));
    if (idx !== -1) return idx;
  }
  return -1;
}

interface ColumnMap {
  nameIdx: number;
  labelIdx: number;
  typeIdx: number;
  descIdx: number;
  optionsIdx: number;
  conditionIdx: number;
  obligatoireIdx: number;
}

function detectColumns(headers: string[]): ColumnMap {
  return {
    nameIdx: detectColumnIndex(headers, ['intitulé', 'intitule', 'titre', 'title', 'élément', 'element', 'nom', 'name']),
    labelIdx: detectColumnIndex(headers, ['question', 'label']),
    typeIdx: detectColumnIndex(headers, ['type']),
    descIdx: detectColumnIndex(headers, ['description', 'aide', 'help', 'detail', 'détail', 'exemple', 'example']),
    optionsIdx: detectColumnIndex(headers, ['options', 'choix', 'choices', 'valeurs', 'values']),
    conditionIdx: detectColumnIndex(headers, ['condition', 'conditionnel']),
    obligatoireIdx: detectColumnIndex(headers, ['obligatoire', 'required', 'requis']),
  };
}

function rowsFromData(data: string[][], colMap: ColumnMap): SmartTableRow[] {
  const hasNameCol = colMap.nameIdx >= 0;
  const hasLabelCol = colMap.labelIdx >= 0;

  const rows: SmartTableRow[] = [];
  for (const row of data) {
    const name = hasNameCol
      ? (row[colMap.nameIdx] ?? '').trim()
      : (!hasLabelCol ? (row[0] ?? '').trim() : '');
    const label = hasLabelCol ? (row[colMap.labelIdx] ?? '').trim() : '';

    if (!name && !label) continue;

    const rawType = colMap.typeIdx >= 0 ? (row[colMap.typeIdx] ?? '').trim() : '';
    const type = rawType ? normalizeType(rawType) : 'document';

    const description = colMap.descIdx >= 0 ? (row[colMap.descIdx] ?? '').trim() : '';

    const rawOptions = colMap.optionsIdx >= 0 ? (row[colMap.optionsIdx] ?? '').trim() : '';
    const options = rawOptions
      ? rawOptions.split(',').map((o) => o.trim()).filter(Boolean)
      : [];

    const conditionRaw = colMap.conditionIdx >= 0 ? (row[colMap.conditionIdx] ?? '').trim() : '';

    const rawObligatoire = colMap.obligatoireIdx >= 0 ? (row[colMap.obligatoireIdx] ?? '').trim().toLowerCase() : '';
    const isRequired = rawObligatoire === '' || !FALSY_VALUES.has(rawObligatoire);

    rows.push({ id: crypto.randomUUID(), name, label, type, description, options, conditionRaw, isRequired });
  }
  return rows;
}

export async function parseCSV(file: File): Promise<SmartTableRow[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          const messages = results.errors.map((e) => e.message).join('; ');
          reject(new Error(`Erreur d'analyse CSV : ${messages}`));
          return;
        }
        const data = results.data as string[][];
        if (data.length < 2) {
          resolve([]);
          return;
        }
        const headers = data[0].map((h) => String(h ?? ''));
        const colMap = detectColumns(headers);
        const bodyRows = data.slice(1).map((r) => r.map((c) => String(c ?? '')));
        resolve(rowsFromData(bodyRows, colMap));
      },
      error: (err) => reject(new Error(err.message)),
    });
  });
}

function findFirstUsableSheet(workbook: XLSX.WorkBook): { sheetName: string; rows: string[][] } | null {
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    const rows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (rows.length >= 2) {
      return { sheetName, rows };
    }
  }
  return null;
}

export async function parseExcel(file: File): Promise<SmartTableRow[]> {
  if (file.size > MAX_EXCEL_FILE_SIZE) {
    return Promise.reject(
      new Error(`Le fichier dépasse la taille maximale autorisée (${MAX_EXCEL_FILE_SIZE / 1024 / 1024} Mo).`)
    );
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) { reject(new Error('Le contenu du fichier est vide ou illisible.')); return; }
        const workbook = XLSX.read(data, { type: 'array' });
        const found = findFirstUsableSheet(workbook);
        if (!found) { resolve([]); return; }
        const { rows } = found;
        const headers = rows[0].map((h) => String(h ?? ''));
        const colMap = detectColumns(headers);
        const bodyRows = rows.slice(1).map((r) => r.map((c) => String(c ?? '')));
        resolve(rowsFromData(bodyRows, colMap));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Impossible de lire le fichier'));
    reader.readAsArrayBuffer(file);
  });
}

export async function parseImportFile(file: File): Promise<SmartTableRow[]> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'xlsx' || ext === 'xls') {
    return parseExcel(file);
  }
  return parseCSV(file);
}

export const ACCEPTED_IMPORT_EXTENSIONS = '.csv,.tsv,.xlsx,.xls';
